// src/pages/patients/PatientsPage.jsx
import React, { useState, useEffect } from 'react';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorDisplay from '../../components/common/ErrorDisplay';
import PatientStatCards from '../../components/common/PatientStatCards';
import Table from '../../components/common/Table';
import Button from '../../components/common/Button';
import { useMedicalCenter } from '../../context/MedicalCenterContext';
import { usePermissions } from '../../hooks/usePermissions';
import { patientService } from '../../services/patientService';
import PatientForm from '../../components/forms/PatientForm';
import { formatDateOnly } from '../../utils/dateUtils';
import { getStatusColor } from '../../utils/statusUtils';
import { useAlert } from '../../context/AlertContext';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';

const PatientsPage = () => {
  const { medicalCenterId } = useMedicalCenter();
  const { canCreate, canEdit, canDelete } = usePermissions();
  const { push } = useAlert();
  const navigate = useNavigate();

  // State hooks must be declared unconditionally (before any early return)
  const [patients, setPatients] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  // Custom confirm dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const loadPatients = async () => {
    try {
      setLoading(true);
      setError(null);
      if (!medicalCenterId) return;

      const patientsData = await patientService.getPatients(medicalCenterId);
      setPatients(patientsData);

      const calculatedStats = await patientService.calculateStats(patientsData, medicalCenterId);
      setStats(calculatedStats);
    } catch (err) {
      console.error('Error loading patients:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!medicalCenterId) return;
    loadPatients();
  }, [medicalCenterId]);

  const handleAddPatient = () => setIsAddModalOpen(true);

  const handleEditPatient = (patient) => {
    setSelectedPatient(patient);
    setIsEditModalOpen(true);
  };

  const handleDeletePatient = (patient) => {
    if (!canDelete('patients')) {
      push('You do not have permission to delete patients', 'warning');
      return;
    }
    setPendingDelete(patient);
    setShowDeleteDialog(true);
  };

  // OPTIMISTIC: remove locally after successful delete; recompute stats
  const confirmDeletePatient = async () => {
    try {
      await patientService.deletePatient(pendingDelete.id, medicalCenterId);
      const next = patients.filter(p => p.id !== pendingDelete.id);
      setPatients(next);
      const nextStats = await patientService.calculateStats(next, medicalCenterId);
      setStats(nextStats);
      push('Patient deleted successfully!', 'success');
    } catch (err) {
      console.error('Error deleting patient:', err);
      push(`Failed to delete patient: ${err.message}`, 'error');
    } finally {
      setShowDeleteDialog(false);
      setPendingDelete(null);
    }
  };

  // OPTIMISTIC: append created patient returned by API; recompute stats
  const handleSubmitPatient = async (patientData) => {
    try {
      setIsSubmitting(true);
      setError(null);

      const created = await patientService.createPatient(patientData, medicalCenterId);
      setIsAddModalOpen(false);

      const next = [created, ...patients];
      setPatients(next);
      const nextStats = await patientService.calculateStats(next, medicalCenterId);
      setStats(nextStats);

      push('Patient created successfully!', 'success');
    } catch (err) {
      console.error('Error adding patient:', err);
      setError(err.message);
      push(`Failed to add patient: ${err.message}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // OPTIMISTIC: merge updated fields into the list; recompute stats
  const handleSubmitEditPatient = async (patientData) => {
    try {
      setIsSubmitting(true);
      setError(null);

      const updated = await patientService.updatePatient(
        selectedPatient.id,
        patientData,
        medicalCenterId
      );

      setIsEditModalOpen(false);
      setSelectedPatient(null);

      const next = patients.map(p => (p.id === updated.id ? { ...p, ...updated } : p));
      setPatients(next);
      const nextStats = await patientService.calculateStats(next, medicalCenterId);
      setStats(nextStats);

      push('Patient updated successfully!', 'success');
    } catch (err) {
      console.error('Error updating patient:', err);
      setError('Failed to update patient: ' + err.message);
      push(`Failed to update patient: ${err.message}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderPatientRow = (patient) => {
    return (
      <tr key={patient.id} className="hover:bg-gray-50">
        <td className="table-cell font-medium">
          <button
            className="text-blue-700 hover:underline"
            onClick={() =>
              navigate(`${ROUTES.CALL_SESSIONS(medicalCenterId)}?patientId=${patient.id}`)
            }
          >
            {patient.first_name} {patient.last_name}
          </button>
        </td>
        <td className="table-cell text-gray-500">{patient.email}</td>
        <td className="table-cell text-gray-500">{patient.phone_number || 'N/A'}</td>
        <td className="table-cell text-gray-500">{formatDateOnly(patient.birthdate)}</td>
        <td className="table-cell">
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(patient.status, 'patient')}`}>
            {patient.status}
          </span>
        </td>
        <td className="table-cell">
          <div className="flex space-x-2">
            {canEdit('patients') && (
              <Button size="sm" variant="outline" onClick={() => handleEditPatient(patient)}>
                Edit
              </Button>
            )}
            {canDelete('patients') && (
              <Button size="sm" variant="danger" onClick={() => handleDeletePatient(patient)}>
                Delete
              </Button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  // Only render the guard after hooks are declared (keeps hooks order stable)
  if (!medicalCenterId) {
    return <div className="p-6 text-gray-600">Select a medical center to continue.</div>;
  }

  if (loading) return <LoadingSpinner className="h-64" />;
  if (error) return <ErrorDisplay error={error} onRetry={loadPatients} />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Patients"
        subtitle="Manage and view all patients in your medical center"
        actionText="Add Patient"
        onAction={handleAddPatient}
        showAction={canCreate('patients')}
      />

      <PatientStatCards stats={stats} loading={loading} />

      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Patient List</h2>
        <Table
          headers={['Name', 'Email', 'Phone', 'Date of Birth', 'Status', 'Actions']}
          data={patients}
          renderRow={renderPatientRow}
          loading={loading}
          emptyMessage="No patients found"
        />
      </div>

      <PatientForm
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleSubmitPatient}
        loading={isSubmitting}
      />

      <PatientForm
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedPatient(null);
        }}
        onSubmit={handleSubmitEditPatient}
        loading={isSubmitting}
        patient={selectedPatient}
      />

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setPendingDelete(null);
        }}
        onConfirm={confirmDeletePatient}
        title="Delete Patient"
        message={`Are you sure you want to delete "${pendingDelete?.first_name} ${pendingDelete?.last_name}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default PatientsPage;